import { pick } from 'ramda';
import { useState } from 'react';
import { FormikHelpers, useFormik } from 'formik';
import { Alert as MuiAlert, Button, FormHelperText, FormLabel, Stack, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import {
  attributesToSubmit,
  initialValues,
  OrgTimeSheetAdjustmentFormData,
  validationSchema,
} from 'src/forms/orgTimeSheetAdjustmentForm';
import NestedTimeSheetPresenter from 'src/presenters/NestedTimeSheetPresenter';
import { IndexParams } from 'src/repositories/organizationAdmin/TimeSheetsRepository';
import { IndexParams as AdjustmentsIndexParams } from 'src/repositories/organizationAdmin/TimeSheetsAdjustmentsRepository';
import { OrgAdminEngagementTimeSheetDates } from 'src/types/resources/OrgAdminEngagementTimeSheetDates';
import { NestedTimeSheet } from 'src/types/resources/TimeSheet';
import { getTimeSheetLongDate, MAX_HOURS_PER_DAY } from 'src/utils/date';
import {
  extractResponseErrors,
  isAxiosError,
  isUnprocessedEntityError,
  parseToFormikErrors,
} from 'src/utils/responseErrors';
import useModals from 'src/hooks/useModals';
import useOrgTimeSheets from 'src/hooks/useOrgTimeSheets';
import useOrgTimeSheetAdjustments from 'src/hooks/useOrgTimeSheetAdjustments';
import useSnackbar from 'src/hooks/useSnackbar';
import Box from 'src/components/Box';
import TimeTrackingTotalTime from 'src/components/TimeTrackingTotalTime';
import TimeFields from 'src/components/TimeTracking/components/TimeFields';
import AlertPayPeriodAmountChanged from 'src/components/CreateTimeSheetAdjustmentForm/components/AlertPayPeriodAmountChanged';

import styles from './styles';

export type OrgTimeSheetAdjustmentFormProps = {
  engagementTimeSheet: OrgAdminEngagementTimeSheetDates;
  parentTimeSheet: NestedTimeSheet;
  searchParams: IndexParams;
  adjustmentsSearchParams: AdjustmentsIndexParams;
};

const OrgTimeSheetAdjustmentForm: React.FC<OrgTimeSheetAdjustmentFormProps> = props => {
  const { engagementTimeSheet, parentTimeSheet, searchParams, adjustmentsSearchParams } = props;

  const [formError, setFormError] = useState<string | null>(null);
  const [focusedTimeField, setFocusedTimeField] = useState<'hours' | 'minutes' | null>(null);

  const { hideModal } = useModals();
  const { enqueueErrorSnackbar } = useSnackbar();
  const { loadEngagementTimeSheetDates, loadTotalEngagementTimeSheet } = useOrgTimeSheets();
  const { createTimeSheetsAdjustment, loadTimeSheetsAdjustments } = useOrgTimeSheetAdjustments();

  const originalHours = Number(parentTimeSheet.hours);

  const handleSubmit = async (
    formData: OrgTimeSheetAdjustmentFormData,
    { setErrors }: FormikHelpers<OrgTimeSheetAdjustmentFormData>,
  ) => {
    setFormError(null);
    try {
      await createTimeSheetsAdjustment(attributesToSubmit(parentTimeSheet.id, formData)).unwrap();
      loadEngagementTimeSheetDates(searchParams);
      loadTotalEngagementTimeSheet(searchParams);
      loadTimeSheetsAdjustments(adjustmentsSearchParams);
      hideModal();
    } catch (error: unknown) {
      if (isAxiosError(error) && isUnprocessedEntityError(error)) {
        const responseErrors = parseToFormikErrors(extractResponseErrors(error)) as Record<string, string>;
        setErrors(pick(['hours', 'minutes', 'clientComment'], responseErrors));
        setFormError(
          responseErrors.parentId
            ? 'This time entry can no longer be adjusted. Reload the page to see its latest state.'
            : responseErrors.nonFieldErrors ?? null,
        );
        return;
      }
      enqueueErrorSnackbar('Failed to save the adjustment');
    }
  };

  const {
    values,
    errors,
    touched,
    isValid,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit: handleFormikSubmit,
    setFieldValue,
  } = useFormik<OrgTimeSheetAdjustmentFormData>({
    initialValues: initialValues(parentTimeSheet),
    validationSchema,
    onSubmit: handleSubmit,
    validateOnChange: true,
    validateOnMount: true,
  });

  const newHours = values.hours + values.minutes;
  const isHoursChanged = newHours !== originalHours;

  const handleHoursChange = async (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const parsedValue = parseInt(event.target.value, 10);
    if (parsedValue > MAX_HOURS_PER_DAY) return;
    await setFieldValue('hours', Number.isNaN(parsedValue) ? 0 : parsedValue);
  };

  const handleMinutesChange = async (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    await setFieldValue('minutes', Number(event.target.value));
  };

  const handleTimeFieldFocus = (fieldName: 'hours' | 'minutes') => () => {
    setFocusedTimeField(fieldName);
  };

  const handleTimeFieldBlur = () => {
    setFocusedTimeField(null);
  };

  const renderDetail = (label: string, value: string) => (
    <Stack sx={styles.formFieldContainer}>
      <Typography sx={styles.label} variant="button" component="span">
        {label}:
      </Typography>
      <Typography sx={styles.value}>{value}</Typography>
    </Stack>
  );

  return (
    <>
      {formError && <MuiAlert severity="error">{formError}</MuiAlert>}
      <form noValidate onSubmit={handleFormikSubmit}>
        <Box sx={styles.wrapper}>
          <Typography sx={styles.title} variant="h3">
            Adjustment: {getTimeSheetLongDate(parentTimeSheet.timekeepingDate)}
          </Typography>
          <Stack sx={styles.details}>
            {renderDetail('Talent', engagementTimeSheet.talent.fullName)}
            {renderDetail('Engagement', engagementTimeSheet.name)}
            {renderDetail('PO', NestedTimeSheetPresenter.poLabel(parentTimeSheet.purchaseOrder))}
          </Stack>
          <Stack sx={styles.hoursRow}>
            <Stack sx={styles.formFieldContainer}>
              <Typography sx={styles.label} variant="button" component="span">
                Original Worked Hours:
              </Typography>
              <TimeTrackingTotalTime sx={styles.value} time={parentTimeSheet.hours} isTotalVisible={false} isSmall />
            </Stack>
            <Stack sx={styles.formFieldContainer}>
              <Typography sx={styles.label} variant="button" component="span">
                New Worked Hours:
              </Typography>
              <TimeFields
                HoursFieldProps={{
                  value: values.hours,
                  error: Boolean(errors.hours),
                  onChange: handleHoursChange,
                  onBlur: handleTimeFieldBlur,
                  onFocus: handleTimeFieldFocus('hours'),
                }}
                MinutesFieldProps={{
                  value: values.minutes,
                  error: Boolean(errors.hours),
                  onChange: handleMinutesChange,
                  onFocus: handleTimeFieldFocus('minutes'),
                }}
                focusedTimeField={focusedTimeField}
              />
            </Stack>
          </Stack>
          {errors.hours && <FormHelperText error>{errors.hours}</FormHelperText>}
          <Box sx={styles.row}>
            <AlertPayPeriodAmountChanged
              originalHours={originalHours}
              newHours={newHours}
              hourlyFee={engagementTimeSheet.hourlyFee}
              viewer="organization"
            />
          </Box>
          <Box sx={styles.row}>
            <FormLabel sx={styles.descriptionLabel} htmlFor="clientComment">
              Description:
            </FormLabel>
            <TextField
              id="clientComment"
              name="clientComment"
              multiline
              rows={2}
              sx={styles.textarea}
              value={values.clientComment}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.clientComment && Boolean(errors.clientComment)}
              helperText={touched.clientComment && errors.clientComment}
            />
          </Box>
          <Box sx={styles.footer}>
            <LoadingButton
              type="submit"
              variant="contained"
              sx={styles.button}
              loading={isSubmitting}
              disabled={!isHoursChanged || !isValid}
            >
              Save
            </LoadingButton>
            <Button variant="outlined" onClick={hideModal} sx={styles.button}>
              Cancel
            </Button>
          </Box>
        </Box>
      </form>
    </>
  );
};

export default OrgTimeSheetAdjustmentForm;
